// ─── ExamplesPanel ───────────────────────────────────────────────────────
// Inline panel (NOT a modal) — renders below the load-input button row in
// Step1Setup. Fetches GET /api/v1/examples on mount; the response inlines
// the full brief envelope per item, so hover-preview and load-on-click are
// both served from one round-trip.

import React, { useEffect, useState } from 'react';
import { X, Loader2, ExternalLink } from 'lucide-react';
import { fetchExamples } from '../../services/api/examplesClient.js';
import BriefPreview from './BriefPreview.jsx';

const previewSnippet = (text, max = 140) => {
  if (!text) return '';
  const t = text.trim().replace(/\s+/g, ' ');
  return t.length > max ? t.slice(0, max - 1) + '…' : t;
};

const ExamplesPanel = ({ contentFormat, tenant, onSelect, onClose, t, color }) => {
  const [items, setItems] = useState(null);   // null = loading
  const [error, setError] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    fetchExamples({ tenant, contentFormat })
      .then(data => { if (!cancelled) setItems(data.examples || []); })
      .catch(err  => { if (!cancelled) setError(err.message || 'Errore nel caricamento degli esempi.'); });
    return () => { cancelled = true; };
  }, [tenant, contentFormat]);

  const hovered = items?.find(e => e.id === hoveredId) || null;

  const card = {
    background: t.inputBg,
    border: `1px solid ${t.border}`,
    borderRadius: '10px',
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    cursor: 'pointer',
    transition: 'border-color 0.15s, transform 0.15s',
  };

  return (
    <div style={{
      marginTop: '10px',
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: '12px',
      padding: '16px',
      position: 'relative',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <span style={{
          fontSize: '11px', fontFamily: "'JetBrains Mono', monospace",
          color: t.textFaint, letterSpacing: '1.2px', textTransform: 'uppercase',
        }}>
          {contentFormat ? `Formato: ${contentFormat}` : ''}
        </span>
        {onClose && (
          <button
            onClick={onClose}
            aria-label="Chiudi pannello esempi"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: t.textFaint, padding: '4px', display: 'flex',
            }}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Loading */}
      {items === null && !error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '20px', color: t.textMuted, fontSize: '13px' }}>
          <Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite', color }} />
          Caricamento esempi…
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.08)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px', padding: '10px 12px',
          color: '#EF4444', fontSize: '12px',
        }}>
          {error}
        </div>
      )}

      {/* Empty */}
      {items && items.length === 0 && !error && (
        <div style={{ padding: '20px', color: t.textMuted, fontSize: '13px', fontStyle: 'italic', textAlign: 'center' }}>
          Nessun esempio disponibile per questo formato.
        </div>
      )}

      {/* Populated */}
      {items && items.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: '14px', alignItems: 'start' }}>
          {/* Cards (left) */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}>
            {items.map(item => {
              const meta = item.metadata || {};
              const brief = item.brief || {};
              const isHovered = item.id === hoveredId;
              return (
                <div
                  key={item.id}
                  onMouseEnter={() => setHoveredId(item.id)}
                  style={{
                    ...card,
                    borderColor: isHovered ? color : t.border,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: t.text, lineHeight: 1.4, flex: 1 }}>
                      {meta.title || item.id}
                    </div>
                    {brief.content_format && (
                      <span style={{
                        fontSize: '10px', fontFamily: "'JetBrains Mono', monospace",
                        padding: '2px 8px', borderRadius: '100px',
                        background: `${color}15`, color, letterSpacing: '0.5px',
                        textTransform: 'uppercase', flexShrink: 0,
                      }}>
                        {brief.content_format}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', color: t.textMuted, lineHeight: 1.5 }}>
                    {previewSnippet(brief.additional_context || brief.topic, 100)}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => onSelect(brief)}
                      style={{
                        padding: '6px 12px', borderRadius: '8px',
                        border: 'none', background: color, color: '#fff',
                        fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      Carica nei campi
                    </button>
                    {meta.output_url && (
                      <a
                        href={meta.output_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          fontSize: '11px', color: t.textMuted,
                          display: 'inline-flex', alignItems: 'center', gap: '3px',
                          textDecoration: 'none',
                        }}
                      >
                        Output reale <ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Preview pane (right, always visible — sticky on tall lists) */}
          <div style={{
            background: t.inputBg,
            border: `1px solid ${t.border}`,
            borderRadius: '10px',
            padding: '14px 16px',
            fontSize: '12px',
            color: t.text,
            maxHeight: '420px',
            overflowY: 'auto',
            position: 'sticky',
            top: 0,
          }}>
            <div style={{ fontSize: '11px', color: t.textFaint, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '1.1px', textTransform: 'uppercase', marginBottom: '8px' }}>
              Anteprima
            </div>
            {!hovered ? (
              <div style={{
                color: t.textFaint, fontStyle: 'italic', lineHeight: 1.5,
                padding: '20px 0', textAlign: 'center',
              }}>
                Passa il mouse su un esempio per vedere i dettagli del brief.
              </div>
            ) : (
              <BriefPreview brief={hovered.brief} t={t} color={color} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamplesPanel;
